import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Form, Button, Row, Col, Badge, Alert, Spinner } from "react-bootstrap";
import FIELD_CONFIG from "./fieldConfig";
import { handleCompanyDetails } from "../../services/companies";
import { handleClaimDetails } from "../../services/claims";
import { useSelector } from "react-redux";
import FraudToggle from "../common/FraudToggle";
import AddressDropdown from "./AddressDropdown";
import { toast } from "react-toastify";

const generateValidationSchema = (userPermissions) => {
  const schema = {};
  Object.entries(FIELD_CONFIG)
    .filter(([key, field]) =>
      userPermissions?.some(
        (p) => p.field_name === field.fieldName && p.is_viewable === "Y",
      ),
    )
    .forEach(([key, field]) => {
      let validator;

      // Determine the base validator based on field type
      if (field.type === "number") {
        validator = Yup.number()
          .typeError(`${field.label} is required`)
          .transform((value, originalValue) => {
            if (originalValue === "" || originalValue === null) {
              return null; // Allow empty strings and null to be null
            }
            const parsed = Number(originalValue);
            return isNaN(parsed) ? undefined : parsed; // Handle invalid numbers
          })
          .nullable(); // Allow null values, but required will override this
      } else if (field.type === "date") {
        validator = Yup.date().nullable();
      } else if (field.type === "textarea") {
        validator = Yup.string()
          .nullable()
          .max(
            field.max ?? 255,
            `${field.label} must be at most ${field.max ?? 255} characters`,
          );
      } else if (field.type === "text") {
        validator = Yup.string()
          .nullable()
          .max(
            field.max ?? 255,
            `${field.label} must be at most ${field.max ?? 255} characters`,
          );
      } else {
        validator = Yup.string().nullable();
      }

      // Check if the user has permission to edit the field
      const hasEditPermission = userPermissions?.some(
        (p) => p.field_name === field.fieldName && p.is_editable === "Y",
      );

      // Only mark as required if the user can edit the field
      if (field.required && hasEditPermission) {
        validator = validator.required(`${field.label} is required`);
      }

      schema[field.fieldName] = validator; // Use field.fieldName as the key
    });
  return Yup.object().shape(schema);
};

const FormField = ({ field, formik, userPermissions }) => {
  if (!field || !formik) return null;

  // Find permission for this field
  const fieldPermission = userPermissions?.find(
    (p) => p.field_name === field.fieldName,
  );

  // If field isn't in permissions or not viewable, don't render
  if (!fieldPermission || fieldPermission.is_viewable !== "Y") {
    return null;
  }

  // Determine if field should be readonly/disabled
  const isDisabled = fieldPermission.is_editable !== "Y";

  switch (field.type) {
    case "textarea":
      // compute current length from formik values (no extra state needed)
      const currentLength = (formik.values[field.fieldName] || "").length;
      return (
        <Form.Group className="mb-3">
          <Form.Label>
            {field.label}
            {field.required && "*"}
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            name={field.fieldName}
            value={formik.values[field.fieldName] || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            isInvalid={
              formik.touched[field.fieldName] && formik.errors[field.fieldName]
            }
            disabled={isDisabled}
            className="form-control"
            maxLength={field.max ?? 255}
          />
          <div className="d-flex justify-content-end mt-1">
            <small className="text-muted">
              {currentLength}/{field.max ?? 500}
            </small>
          </div>
          <Form.Control.Feedback type="invalid">
            {formik.errors[field.fieldName]}
          </Form.Control.Feedback>
        </Form.Group>
      );

    case "select": // Keep select logic for other fields that still use it.
      return (
        <Form.Group className="mb-3">
          <Form.Label>
            {field.label}
            {field.required && "*"}
          </Form.Label>
          <Form.Select
            name={field.fieldName}
            value={formik.values[field.fieldName] || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            isInvalid={
              formik.touched[field.fieldName] && formik.errors[field.fieldName]
            }
            disabled={isDisabled}
            className="form-select"
          >
            <option value="">Select An Option</option>
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">
            {formik.errors[field.fieldName]}
          </Form.Control.Feedback>
        </Form.Group>
      );

    case "toggle":
      return (
        <Form.Group className="mb-3">
          <FraudToggle
            checked={formik.values[field.fieldName] || false}
            onChange={(e) => {
              formik.setFieldValue(field.fieldName, e.target.checked);
            }}
            disabled={isDisabled}
            label={field.label}
          />
        </Form.Group>
      );

    case "address":
      return (
        <AddressDropdown
          policyId={formik.values.policy_id}
          selectedAddressId={formik.values[field.fieldName]}
          onSelectAddress={(addressId) => {
            formik.setFieldValue(field.fieldName, addressId);
          }}
          onAddNewAddress={(id) => {
            formik.setFieldValue(field.fieldName, id);
            setTimeout(() => {
              formik.handleSubmit();
            }, 500);
            // Refresh addresses after adding new one
          }}
        />
      );

    case "date":
      return (
        <Form.Group className="mb-3">
          <Form.Label>
            {field.label}
            {field.required && "*"}
          </Form.Label>
          <Form.Control
            type={field.type}
            name={field.fieldName}
            value={formik.values[field.fieldName] || ""}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            isInvalid={
              formik.touched[field.fieldName] && formik.errors[field.fieldName]
            }
            disabled={isDisabled}
            step="any"
            className="form-control"
            min="1700-01-01"
            max="9999-12-31"
          />
          <Form.Control.Feedback type="invalid">
            {formik.errors[field.fieldName]}
          </Form.Control.Feedback>
        </Form.Group>
      );

    default: // Handles text and number inputs
      return (
        <Form.Group className="mb-3">
          <Form.Label>
            {field.label}
            {field.required && "*"}
          </Form.Label>
          <Form.Control
            type={field.type}
            name={field.fieldName}
            value={formik.values[field.fieldName] || ""}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            isInvalid={
              formik.touched[field.fieldName] && formik.errors[field.fieldName]
            }
            disabled={isDisabled}
            step="any"
            className="form-control"
          />
          <Form.Control.Feedback type="invalid">
            {formik.errors[field.fieldName]}
          </Form.Control.Feedback>
        </Form.Group>
      );
  }
};

function ClaimForm({ initialValues = {}, onUpdate, handleClose }) {
  const userDetails = useSelector((state) => state.auth.userDetails);
  const { permissions: userPermissions, role: userRole } = userDetails;

  const [companiesData, setCompaniesData] = React.useState([]);
  const [lawFirmName, setLawFirmName] = React.useState("");
  const [showAlert, setShowAlert] = React.useState(false);

  const getCompaniesData = async () => {
    try {
      const response = await handleCompanyDetails.getCompanies({
        limit: 1000,
      });
      setCompaniesData(response.companies_data || []);
      const LawFirm = response.companies_data.find(
        (lawFirm) => lawFirm.id === initialValues.law_firm_id,
      );
      setLawFirmName(LawFirm.title);
    } catch (error) {
      console.error("Error fetching companies:", error);
      setCompaniesData([]);
    }
  };

  React.useEffect(() => {
    getCompaniesData();
  }, []);

  const defaultValues = Object.values(FIELD_CONFIG).reduce((acc, field) => {
    // Set appropriate default values based on field type
    if (field.type === "toggle") {
      acc[field.fieldName] = false;
    } else {
      acc[field.fieldName] = "";
    }
    return acc;
  }, {});

  const formik = useFormik({
    initialValues: {
      ...defaultValues,
      ...initialValues,
    },
    validationSchema: generateValidationSchema(userPermissions),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const submitData = {
          ...values,
          id: initialValues.id,
          status: "Active",
        };
        const response = await handleClaimDetails.updateDetails(submitData);

        if (response.status === "Success") {
          toast.success("Claim updated successfully");
          onUpdate();
          handleClose();
        } else {
          toast.error("Failed to update claim: " + response.message);
          console.error("Failed to update claim:", response.message);
        }
      } catch (error) {
        toast.error("An error occurred while updating the claim");
        console.error("Error updating claim:", error);
      } finally {
        setSubmitting(false);
      }
    },
    enableReinitialize: true,
  });

  if (!FIELD_CONFIG) return null;

  // Check if user has permission to edit the fraud toggle
  const fraudFieldPermission = userPermissions?.find(
    (p) => p.field_name === "is_fraudulent",
  );
  const canEditFraudToggle = fraudFieldPermission?.is_editable === "Y";

  // Handle fraud toggle change - immediately save to backend
  const handleFraudToggleChange = async (e) => {
    const newValue = e.target.checked;

    // Update formik state immediately for UI feedback
    formik.setFieldValue("is_fraudulent", newValue);

    try {
      // Prepare data for API call
      const updateData = {
        id: initialValues.id,
        is_fraudulent: newValue,
      };

      // Call the API to update the backend
      const response = await handleClaimDetails.updateDetails(updateData);

      if (response.status === "Success") {
        setShowAlert(true);
        // Auto-hide alert after 3 seconds
        setTimeout(() => setShowAlert(false), 3000);

        // Optionally refresh the parent component
        if (onUpdate) {
          onUpdate();
        }
      } else {
        console.error("Failed to update fraud status:", response.message);
        // Revert the change if API call failed
        formik.setFieldValue("is_fraudulent", !newValue);
        alert("Failed to update fraud status. Please try again.");
      }
    } catch (error) {
      console.error("Error updating fraud status:", error);
      // Revert the change if API call failed
      formik.setFieldValue("is_fraudulent", !newValue);
      alert("Error updating fraud status. Please try again.");
    }
  };

  const isError = Object.keys(formik.errors).length > 0;

  return (
    <Form onSubmit={formik.handleSubmit} className="w-100">
      <h4 className="text-left mb-4">
        Claim # {formik.values["claim_number"]}
      </h4>
      <h5 className="text-left mb-3">Assigned to: {lawFirmName}</h5>

      {/* Alert for claim status update */}
      {showAlert && (
        <Alert
          variant="success"
          className="mb-3"
          dismissible
          onClose={() => setShowAlert(false)}
        >
          Claim Status updated
        </Alert>
      )}

      {/* Fraud Toggle under Assigned to - Visible only to Admin (userRole === 1) */}
      {userRole === "Admin" && (
        <div className="mb-4 d-flex align-items-center">
          {/* Badge on the far left */}
          {formik.values["is_fraudulent"] && (
            <Badge bg="danger" className="d-flex align-items-center me-3">
              <i className="fas fa-exclamation-triangle me-1"></i>
              Fraudulent
            </Badge>
          )}

          {/* Label */}
          <span className="me-3 fw-bold">Is Fraudulent claim?</span>

          {/* Toggle with Yes/No labels */}
          <div className="d-flex align-items-center">
            <span className="me-2 small">No</span>
            <FraudToggle
              checked={formik.values["is_fraudulent"] || false}
              onChange={handleFraudToggleChange}
              disabled={!canEditFraudToggle}
              label=""
            />
            <span className="ms-2 small">Yes</span>
          </div>
        </div>
      )}

      <Row>
        {Object.values(FIELD_CONFIG)
          .filter((fieldConfig) => fieldConfig.fieldName !== "is_fraudulent") // Exclude fraud toggle from regular fields
          .map((fieldConfig) => {
            const isAllowed = userPermissions?.some(
              (p) =>
                p.field_name === fieldConfig.fieldName && p.is_viewable === "Y",
            );

            if (!isAllowed) return null;

            return (
              <Col
                md={fieldConfig.col || 12}
                className="mb-3"
                key={fieldConfig.fieldName}
              >
                <FormField
                  field={fieldConfig} // Pass the entire fieldConfig
                  formik={formik}
                  userRole={userRole}
                  userPermissions={userPermissions}
                  companiesData={companiesData}
                />
              </Col>
            );
          })}
      </Row>

      <div className="mt-4">
        <div className="d-flex gap-3 justify-content-center">
          <div className="d-flex w-50">
            <Button className="btn cancel-btn" onClick={handleClose}>
              Cancel
            </Button>
          </div>
          <div className="w-50">
            <Button
              className="btn auth-page-btn"
              type="submit"
              disabled={formik.isSubmitting || isError}
            >
              {formik.isSubmitting && <Spinner size="sm" className='me-1' />} Save
            </Button>
            {isError && (
              <small className="text-danger small d-block mt-1">
                Please fix the errors above before submitting.
              </small>
            )}
          </div>
        </div>
      </div>
    </Form>
  );
}

export default ClaimForm;
